/**
 * SSO Configuration
 * Centraliza todas las configuraciones relacionadas con SSO
 * Principio: Single Responsibility - Solo configuración
 */

export const SSOConfig = {
  // URLs del backend SSO
  BACKEND_URL: {
    PROD: process.env.REACT_APP_BASE_URL_PROD,
    DEV: process.env.REACT_APP_BASE_URL_DEV,
  },

  // Proveedores SSO soportados
  PROVIDERS: {
    OPENEDX: 'openedx',
    CANVAS: 'canvas',
  },

  // Endpoints SSO por proveedor
  SSO_ENDPOINTS: {
    openedx: '/api/auth/connect/by-origin',
    canvas: '/api/auth/canvas/connect/by-origin',
  },

  // Parámetros SSO
  SSO_PARAMS: {
    RESPONSE_MODE: 'redirect', // Valor fijo para response_mode
  },

  // Timeouts y reintentos
  REQUEST_TIMEOUT: 5000, // 5 segundos
  MAX_RETRIES: 0, // NO reintentar para evitar colapso del backend

  // Almacenamiento local
  STORAGE_KEYS: {
    SSO_ATTEMPTED: 'owly_sso_attempted',
    SSO_FAILED: 'owly_sso_failed',
    SSO_SUCCESS: 'owly_sso_success',
  },

  // TTL para intentos (evitar múltiples intentos en la misma sesión)
  ATTEMPT_TTL_MS: 3600000, // 1 hora

  // Estados SSO
  STATUS: {
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    NO_REFERER: 'NO_REFERER',
    INVALID_DOMAIN: 'INVALID_DOMAIN',
    BACKEND_ERROR: 'BACKEND_ERROR',
  },
};

/**
 * Detecta el proveedor SSO basado en el hostname original del referer
 * @param {string} originalHostname - Hostname original del referer (ej: canvas.aulasneo.com)
 * @returns {string} Tipo de proveedor ('openedx', 'canvas', etc.)
 */
export function detectSSProvider(originalHostname) {
  if (!originalHostname) {
    return SSOConfig.PROVIDERS.OPENEDX; // Default fallback
  }

  // Detectar Canvas si el hostname contiene 'canvas'
  if (originalHostname.toLowerCase().includes('canvas')) {
    return SSOConfig.PROVIDERS.CANVAS;
  }

  // Default a OpenedX para todos los demás casos
  return SSOConfig.PROVIDERS.OPENEDX;
}

/**
 * Obtiene el endpoint SSO según el proveedor
 * @param {string} provider - Tipo de proveedor
 * @returns {string} Endpoint correspondiente
 */
export function getSSEndpoint(provider) {
  return SSOConfig.SSO_ENDPOINTS[provider] || SSOConfig.SSO_ENDPOINTS.openedx;
}

/**
 * Obtiene la URL base del backend según el entorno
 */
export function getBackendBaseUrl() {
  const isProduction = process.env.NODE_ENV === 'production';
  return isProduction ? SSOConfig.BACKEND_URL.PROD : SSOConfig.BACKEND_URL.DEV;
}

/**
 * Obtiene la URL completa del endpoint SSO según el proveedor
 * @param {string} provider - Tipo de proveedor
 * @returns {string} URL completa del endpoint
 */
export function getSSOEndpointUrl(provider) {
  const baseUrl = getBackendBaseUrl();
  const endpoint = getSSEndpoint(provider);
  return `${baseUrl}${endpoint}`;
}

/**
 * Construye la URL completa del SSO con los parámetros
 * @param {string} hostDomain - Dominio base del referer (ej: canvas.aulasneo.com)
 * @param {string} provider - Tipo de proveedor
 * @returns {string} URL completa con parámetros
 */
export function buildSSOUrl(hostDomain, provider) {
  const baseUrl = getSSOEndpointUrl(provider);
  const params = new URLSearchParams({
    host_domain: hostDomain,
    response_mode: SSOConfig.SSO_PARAMS.RESPONSE_MODE,
  });
  
  return `${baseUrl}?${params.toString()}`;
}

/**
 * Verifica si el SSO ya fue intentado recientemente
 */
export function hasSSOBeenAttempted() {
  try {
    const attempted = localStorage.getItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
    if (!attempted) return false;

    const attemptData = JSON.parse(attempted);
    const now = Date.now();
    const elapsed = now - attemptData.timestamp;

    // Si pasó más del TTL, permitir nuevo intento
    if (elapsed > SSOConfig.ATTEMPT_TTL_MS) {
      localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
      return false;
    }

    return true;
  } catch (error) {
    console.warn('[SSO Config] Error checking attempt:', error);
    return false;
  }
}

/**
 * Marca que el SSO fue intentado
 */
export function markSSOAttempted(status = SSOConfig.STATUS.PENDING) {
  try {
    const attemptData = {
      timestamp: Date.now(),
      status,
    };
    localStorage.setItem(
      SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED,
      JSON.stringify(attemptData)
    );
  } catch (error) {
    console.warn('[SSO Config] Error marking attempt:', error);
  }
}

/**
 * Limpia los flags de SSO
 */
export function clearSSOFlags() {
  try {
    localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_ATTEMPTED);
    localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_FAILED);
    localStorage.removeItem(SSOConfig.STORAGE_KEYS.SSO_SUCCESS);
  } catch (error) {
    console.warn('[SSO Config] Error clearing flags:', error);
  }
}
